import { AssistantProductLayout } from '@/components/dashboard/assistant-product-layout';
import { pendingDeletionQuery } from '@/features/privacy/pending-deletion-query';
import {
  type ErrorComponentProps,
  Outlet,
  createFileRoute,
  redirect,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { AppBootSplash } from '@/components/app-boot-splash';
import { IdleTimeoutGuard } from '@/components/app/idle-timeout-guard';
import { FunnelTransition, routeZone } from '@/components/app/route-transition';
import { StateError } from '@/components/app/state-error';
import { identifyUser } from '@/components/posthog-provider';
import { ImpersonationBanner } from '@/features/admin-terminal/components/impersonation-banner';
import { BackgroundAnalysisProvider } from '@/features/assets/background-analysis';
import { BillingRecoveryFrame } from '@/features/billing/components/billing-recovery-frame';
import { ClaireWalkthroughProvider } from '@/features/claire/lib/walkthrough-provider';
import { MetaSyncProvider } from '@/features/meta-sync';
import { ActiveLocationScope } from '@/features/organization-locations';
import { authRedirectSearchValue } from '@/lib/auth-redirect';
import { setCapgoCustomId } from '@/lib/capgo';
import {
  markSessionSeen,
  navigatingAway,
  startDomainHandoffOnce,
} from '@/lib/domain-handoff';
import { intercomLogin } from '@/lib/intercom';
import {
  SessionUnavailableError,
  ensureSession,
  useSession,
} from '@/lib/session';

/**
 * `_authed` route context is empty for now. `beforeLoad` guards every child by
 * resolving the session and redirecting to `/sign-in` (with the intended URL
 * preserved via `?redirect=`) when there is no signed-in user. Child routes
 * (2B–2F) can read the session via `useSession()` directly.
 */
export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ location, context }) => {
    const session = await ensureSession();

    // The API could not be reached (it already retried). This is NOT a sign-out
    // — `auth/session` returns 200 `{ user: null }` for that — so bouncing to
    // /sign-in here would throw away a valid session on a transient blip. Fail
    // into `AuthedRouteError`, which reconnects and keeps the session.
    if (session.unavailable) {
      throw new SessionUnavailableError();
    }

    if (!session.user) {
      // Auth-free record hand-off: a valid upload token (magic link scanned on
      // a phone that isn't logged in) grants scoped access to the record
      // screen, which validates the token itself. Let it through instead of
      // bouncing to /sign-in.
      const search = location.search as { uploadToken?: unknown };
      const hasUploadToken =
        typeof search?.uploadToken === 'string' &&
        search.uploadToken.length > 0;
      if (location.pathname.startsWith('/record/') && hasUploadToken) {
        return;
      }

      // First visit to borrah.com from a browser that was signed in on
      // borradh.io: bring that session across instead of asking for a
      // password. Runs at most once per browser; see domain-handoff.ts.
      if (startDomainHandoffOnce(authRedirectSearchValue(location))) {
        return navigatingAway();
      }

      throw redirect({
        to: '/sign-in',
        search: { redirect: authRedirectSearchValue(location) },
      });
    }

    markSessionSeen();
    if (session.user?.organizationId) {
      const pending = await context.queryClient
        .ensureQueryData(pendingDeletionQuery(session.user.id))
        .catch(() => null);
      if (pending?.pending) throw redirect({ to: '/restore-organization' });
    }

    // A user belongs to exactly one organization, and it is a property of the
    // person — so there is nothing to activate and nothing to recover from.
    // This used to set the active org, refetch the session and hope: onboarding
    // set it best-effort, so a flaky network left a user whose every org-scoped
    // request returned 400, surfacing as "Failed to load/create conversation".
    //
    // accept-invitation runs before the user belongs to any org, so it is exempt.
    if (
      !session.user?.organizationId &&
      !location.pathname.startsWith('/accept-invitation')
    ) {
      throw redirect({ to: '/onboarding' });
    }
  },
  errorComponent: AuthedRouteError,
  component: AuthedLayout,
});

function AuthedRouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  // A deploy can briefly interrupt auth/session even though this browser still
  // holds a valid bearer. Do not ask the customer to repair our rollout: retry
  // the route in the background until the next healthy API machine responds.
  // Other route errors retain the normal actionable error screen below.
  if (error instanceof SessionUnavailableError) {
    return <ReconnectSession reset={reset} router={router} />;
  }

  const retry = () => {
    reset();
    void router.invalidate();
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      <StateError
        className="w-full max-w-lg"
        message="We could not load your session. Please try again."
        onRetry={retry}
      />
    </div>
  );
}

function ReconnectSession({
  reset,
  router,
}: Pick<ErrorComponentProps, 'reset'> & {
  router: ReturnType<typeof useRouter>;
}) {
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // `ensureSession` already makes three bounded requests. Leave a small,
    // increasing gap between groups so a deploy handover recovers without
    // customer action and without turning one browser into a request loop.
    const delayMs = Math.min(500 * 2 ** Math.min(attempt, 4), 8_000);
    const timer = window.setTimeout(() => {
      setAttempt((current) => current + 1);
      reset();
      void router.invalidate();
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [attempt, reset, router]);

  return <AppBootSplash message="Reconnecting securely…" />;
}

function AuthedLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data } = useSession();
  const user = data?.user;
  const intercomJwt = data?.session?.intercomJwt;
  const impersonatedBy = data?.session?.impersonatedBy;

  // Identify the user in Intercom once the authenticated session lands. Re-runs
  // when the JWT changes (e.g., session refresh). On cold start the bearer is
  // rehydrated from secure storage in `main.tsx`, so this effect fires before
  // the user can open the support widget.
  useEffect(() => {
    if (user) {
      // Tie PostHog events to the real user. The `organization` group is set
      // separately by the dashboard layouts once the active org resolves.
      identifyUser(
        user.id,
        { email: user.email, name: user.name },
        {
          impersonatedBy,
          isAdmin: user.role === 'admin' || user.role === 'scoped-admin',
          organizationId: user.organizationId,
        }
      );
      void intercomLogin(user.id, user.email, user.name, intercomJwt);
    }
  }, [user, intercomJwt, impersonatedBy]);

  // Tag the device in Capgo with the user's email so internal testers can
  // find it in the Capgo dashboard's device list (native only; no-op on web).
  useEffect(() => {
    if (user?.email) void setCapgoCustomId(user.email);
  }, [user?.email]);

  // Match Next `(protected)/*`: only `/dashboard/*` gets the app sidebar
  // (added by `DashboardLayoutShell` inside the dashboard route). `/assistant`
  // has its own product layout. Everything else — `/connect`, `/create-video`,
  // `/sequences`, `/record`, `/ads`, `/settings`,
  // `/accept-invitation` — renders bare; each route owns its chrome.
  const chrome = pathname.startsWith('/assistant') ? (
    <AssistantProductLayout />
  ) : (
    <Outlet />
  );

  return (
    <MetaSyncProvider>
      {/* Publishes the active branch to the api-client before any child query
          fires. Everything below this point is branch-scoped. */}
      <ActiveLocationScope>
        <BackgroundAnalysisProvider>
          <BillingRecoveryFrame>
            <ImpersonationBanner />
            <IdleTimeoutGuard />
            <ClaireWalkthroughProvider handlers={{}}>
              {/* Entering and leaving a create/edit editor swaps the whole screen,
              so it cross-fades. Moving between dashboard pages does not change
              zone, so the shell is never torn down for it. */}
              <FunnelTransition zone={routeZone(pathname)}>
                {chrome}
              </FunnelTransition>
            </ClaireWalkthroughProvider>
          </BillingRecoveryFrame>
        </BackgroundAnalysisProvider>
      </ActiveLocationScope>
    </MetaSyncProvider>
  );
}
